import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, ReactNode, useMemo } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Caret } from './Caret';
import { CollapsibleContent as Content } from './Content';
import { useCollapsible } from './useCollapsible';

export const CollapsiblePanel = memo(
  ({
    header,
    children,
    style,
    headerContainerStyle,
    headerStyle,
    contentContainerStyle,
    caretColor,
    caretSize,
    caretStyle,
  }: {
    style?: StyleProp<ViewStyle>;
    headerContainerStyle?: StyleProp<ViewStyle>;
    headerStyle?: StyleProp<ViewStyle>;
    contentContainerStyle?: StyleProp<ViewStyle>;
    caretStyle?: StyleProp<ViewStyle>;
    header: ReactNode;
    children: ReactNode;
    caretColor?: string;
    caretSize?: number;
  }) => {
    const styles = useStyles();

    const {
      onLayout,
      animatedStyle,
      caretStyle: caretAnimatedStyle,
      onPress,
    } = useCollapsible();

    return (
      <View style={[styles.container, style]}>
        <TouchableOpacity
          onPress={onPress}
          hitSlop={{ left: 0, right: 0, top: 8, bottom: 8 }}
          style={[styles.headerContainer, headerContainerStyle]}
        >
          <HStack
            style={headerStyle}
            justifyContent={'flex-start'}
            css={`
              flex: 1;
            `}
          >
            {header}
          </HStack>
          <Caret
            style={[caretAnimatedStyle, caretStyle]}
            color={caretColor}
            size={caretSize}
          />
        </TouchableOpacity>
        <Content
          onLayout={onLayout}
          contentStyle={[styles.content, contentContainerStyle]}
          style={[animatedStyle]}
        >
          {children}
        </Content>
      </View>
    );
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        container: {
          paddingTop: rpx(15),
          paddingBottom: rpx(15),
        },
        headerContainer: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          paddingLeft: rpx(15),
          paddingRight: rpx(15),
        },
        content: {
          paddingLeft: rpx(15),
          paddingRight: rpx(15),
        },
      }),
    [rpx],
  );
};
